import { createContext, useContext, useState, useEffect } from "react";
import { UserSettingsApi } from "../api/UserSettingsApi";
import { logger } from "../utils/logger";
import { getErrorMessage } from "../utils/errorMessages";
import { toast } from "sonner";

const UserSettingsContext = createContext();

export const useUserSettings = () => {
  return useContext(UserSettingsContext);
};

export const UserSettingsProvider = ({ children }) => {
  const [householdSize, setHouseholdSize] = useState(null);
  const [weekStartDay, setWeekStartDay] = useState("MONDAY");
  const [loading, setLoading] = useState(true);

  const fetchUserSettings = async () => {
    try {
      setLoading(true);
      const response = await UserSettingsApi.fetchUserSettings();
      setHouseholdSize(response.householdSize);
      setWeekStartDay(response.weekStartDay ?? "MONDAY");
    } catch (error) {
      if (error.response?.status === 404) {
        logger.info("No user settings found");
        setHouseholdSize(null);
        setWeekStartDay("MONDAY");
      } else {
        const correlationId = error.response?.headers?.["x-correlation-id"];
        logger.error("Failed to fetch user settings", error, { correlationId });
        toast.error(getErrorMessage(error));
      }
    } finally {
      setLoading(false);
    }
  };

  const updateHouseholdSize = async (newHouseholdSize) => {
    const previous = householdSize;
    setHouseholdSize(newHouseholdSize);
    try {
      await UserSettingsApi.updateUserSettings(newHouseholdSize, weekStartDay);
      toast.success("Household size updated successfully");
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      logger.error("Failed to update household size", error, { correlationId, newHouseholdSize });
      toast.error(getErrorMessage(error));
      setHouseholdSize(previous);
    }
  };

  const updateWeekStartDay = async (newWeekStartDay) => {
    const previous = weekStartDay;
    setWeekStartDay(newWeekStartDay);
    try {
      await UserSettingsApi.updateUserSettings(householdSize, newWeekStartDay);
      toast.success("Week start day updated");
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      logger.error("Failed to update week start day", error, { correlationId, newWeekStartDay });
      toast.error(getErrorMessage(error));
      setWeekStartDay(previous);
    }
  };

  const value = {
    householdSize,
    weekStartDay,
    loading,
    fetchUserSettings,
    updateHouseholdSize,
    updateWeekStartDay,
  };

  useEffect(() => {
    fetchUserSettings();
  }, []);

  return (
    <UserSettingsContext.Provider value={value}>
      {children}
    </UserSettingsContext.Provider>
  );
};
